import React, { useEffect, useState } from "react";


import {
  buildWeeklyTrend,
  buildInterviewPrediction,
  estimateOfferProbability,
} from "./analysisHelpers";
import "./AnalysisDashboardTop.css";
import "./Analysis.css"; // reuse same styles (or create new css if you want)
// use same backend base you use elsewhere
const API_BASE = "https://job.scae.co"; // or "http://localhost:5001"




const AnalysisDashboard = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // load jobs from backend
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/jobs`);
        if (!res.ok) throw new Error("Failed to fetch jobs");
        const data = await res.json();
        setApps(data);
      } catch (err) {
        console.error(err);
        setError("Could not load jobs");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

if (loading) return <div className="analysis-wrapper">Loading...</div>;
  if (error) return <div className="analysis-wrapper">{error}</div>;
  if (!apps.length)
    return (
      <div className="analysis-wrapper">
        <h2 className="analysis-title">Job Search Analysis</h2>
        <div>No applications yet.</div>
      </div>
    );



  // --- 1. Basic counts from your data ---
  const totalApps = apps.length;

  const countByStatus = (statusList) =>
    apps.filter((a) => statusList.includes(a.status)).length;

  const offers = countByStatus(["Offer"]);
  const rejectedOrGhosted = countByStatus(["Rejected", "Ghosted"]);
  const inProcess = countByStatus([
    "Applied",
    "Recruiter Contacted",
    "Assessment Sent",
    "Phone Screen",
    "Technical Interview",
    "Final Interview",
  ]);

  // Jobs where you have matchScore
  const jobsWithScore = apps.filter(
    (a) => a.matchScore !== null && a.matchScore !== undefined
  );

  const avgMatchScore =
    jobsWithScore.length > 0
      ? Math.round(
          jobsWithScore.reduce((sum, j) => sum + (j.matchScore || 0), 0) /
            jobsWithScore.length
        )
      : null;

  // --- 2. Theoretical probabilities (from our text) ---
  const PROB_INTERVIEW = 0.10; // 10% per application
  const PROB_OFFER_AFTER_INTERVIEW = 0.25; // 25%
  const PROB_PER_APP = PROB_INTERVIEW * PROB_OFFER_AFTER_INTERVIEW; // 2.5%
  const TARGET_APPS_PER_OFFER = Math.round(1 / PROB_PER_APP); // ≈ 40

  // How many applications in this "cycle" (since you started using tool)
  const appsPerOffer = TARGET_APPS_PER_OFFER; // 40 baseline
  const offerCyclesCompleted = offers; // each offer = one cycle "won"
  const appsThisCycle = totalApps - offerCyclesCompleted * appsPerOffer;
  const progressToNextOffer = Math.min(
    100,
    Math.round((appsThisCycle / appsPerOffer) * 100)
  );

  // Expected offers vs actual (based on math)
  const expectedOffers = totalApps * PROB_PER_APP;
  const expectedOffersRounded = expectedOffers.toFixed(1);

  // --- 3. Skill / missing skill aggregates ---

  // Flatten arrays safely
  const allSkills = apps.flatMap((job) => job.skills || []);
  const allMissing = apps.flatMap((job) => job.missing || []);

  const countFreq = (arr) => {
    const m = new Map();
    arr.forEach((item) => {
      const key = item.trim();
      if (!key) return;
      m.set(key, (m.get(key) || 0) + 1);
    });
    return [...m.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8); // top 8
  };

  const topSkills = countFreq(allSkills);
  const topMissing = countFreq(allMissing);

  // --- 4. Helper: get width for "bar" (relative to max count) ---
  const getBarWidth = (index, list) => {
    if (!list.length) return "0%";
    const max = list[0][1];
    if (!max) return "0%";
    const value = list[index][1];
    return `${(value / max) * 100}%`;
  };

  // --- 5. Top jobs by matchScore ---
  const topJobs = [...jobsWithScore]
    .sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0))
    .slice(0, 5);

  // use your story: ~1 offer per 30–50 apps → target 40

    const targetForNextOffer = 50;
    const progressToOffer = Math.min(totalApps / targetForNextOffer, 1);

    const weeklyTrend = buildWeeklyTrend(apps);
    const prediction = buildInterviewPrediction(apps);


    // global interview rate for offer probability
    const globalInterviewRate =
    prediction?.interviewRate && prediction.interviewRate > 0
        ? prediction.interviewRate
        : 0.1;

    // sort apps by "strength" for offer probability table
    const jobsWithProb = apps
    .map((job) => {
        const p = estimateOfferProbability(job, globalInterviewRate);
        return { ...job, offerProb: p };
    })
    .sort((a, b) => b.offerProb - a.offerProb);

  return (
    <div className="analysis-wrapper">
      <h2 className="analysis-title">Job Analysis</h2>
      {/* TOP SUMMARY + PROGRESS TO NEXT OFFER */}
        <div className="analysis-grid">
        <div className="analysis-card">
          <div className="card-label">Total Applications</div>
          <div className="card-value">{totalApps}</div>
        </div>

        <div className="analysis-card">
          <div className="card-label">In Process</div>
          <div className="card-value">{inProcess}</div>
        </div>

        <div className="analysis-card">
          <div className="card-label">Offers</div>
          <div className="card-value card-value-positive">{offers}</div>
        </div>

        <div className="analysis-card">
          <div className="card-label">Rejected / Ghosted</div>
          <div className="card-value card-value-negative">
            {rejectedOrGhosted}
          </div>
        </div>
      </div>

      {/* PROGRESS LINE TO NEXT OFFER */}
      <div className="analysis-section">
        <h3 className="section-title">Progress Toward Next Offer</h3>
        <p className="section-sub">
          Based on your profile: ~1 offer every 30–50 targeted applications
          (target: {targetForNextOffer}).
        </p>

        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${progressToOffer * 100}%` }}
          />
        </div>
        <div className="progress-text">
          {totalApps} / {targetForNextOffer} applications so far{" "}
          {progressToOffer >= 1 ? "→ you’re in the offer window now." : ""}
        </div>
      </div>
      {/* WEEKLY TREND */}
      <div className="analysis-section">
        <h3 className="section-title">Weekly Trend</h3>
        {weeklyTrend.length === 0 ? (
          <div>No date_applied data yet.</div>
        ) : (
          <div className="trend-list">
            {weeklyTrend.slice(-8).map((w) => (
              <div key={w.key} className="trend-row">
                <span className="trend-week">{w.label}</span>
                <span>{w.applications} apps</span>
                <span>{w.interviews} interviews</span>
                <span>{w.offers} offers</span>
              </div>
            ))}
          </div>
        )}
      </div>    


      <div className="analysis-row">
        {/* Chance summary */}
        <div className="analysis-card">
          <div className="analysis-title">Your Job Odds (Math View)</div>
          <div className="analysis-main-number">
            {Math.round(PROB_PER_APP * 1000) / 10}%{" "}
            <span className="analysis-main-sub">chance per application</span>
          </div>
          <div className="analysis-subline">
            ~{appsPerOffer} applications → 1 offer (average)
          </div>
          <ul className="analysis-list">
            <li>Interview chance per app: {PROB_INTERVIEW * 100}%</li>
            <li>Offer chance after interview: {PROB_OFFER_AFTER_INTERVIEW * 100}%</li>
            <li>
              Expected offers so far: {expectedOffersRounded} (actual: {offers})
            </li>
          </ul>
        </div>

        {/* Offer cycle progress */}
        <div className="analysis-card">
          <div className="analysis-title">Progress to Next Offer</div>
          <div className="analysis-subline">
            Cycle {offerCyclesCompleted + 1}:{" "}
            <strong>
              {appsThisCycle}/{appsPerOffer}
            </strong>{" "}
            applications
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progressToNextOffer}%` }}
            />
          </div>
          <div className="analysis-subtext">
            Keep pushing. Every app is a lottery ticket with skill behind it.
          </div>
        </div>

        {/* MatchScore summary */}
        <div className="analysis-card">
          <div className="analysis-title">Match Score Overview</div>
          <div className="analysis-main-number">
            {avgMatchScore !== null ? `${avgMatchScore}%` : "--"}
          </div>
          <div className="analysis-subline">
            Avg match across {jobsWithScore.length} scored jobs
          </div>
          <ul className="analysis-list">
            <li>Total applications: {totalApps}</li>
            <li>In process: {inProcess}</li>
            <li>Offers: {offers}</li>
            <li>Rejected/Ghosted: {rejectedOrGhosted}</li>
          </ul>
        </div>
      </div>
      {/* ROW 3: Job offer pipeline line */}
      <div className="analysis-row">
        <div className="analysis-card">
          <div className="analysis-title">Job Offer Pipeline</div>
          <div className="pipeline-wrapper">
            <div className="pipeline-step">
              <div className="pipeline-number">{totalApps}</div>
              <div className="pipeline-label">Applications</div>
            </div>
            <div className="pipeline-arrow">➜</div>
            <div className="pipeline-step">
              <div className="pipeline-number">
                {countByStatus([
                  "Recruiter Contacted",
                  "Assessment Sent",
                  "Phone Screen",
                  "Technical Interview",
                  "Final Interview",
                ])}
              </div>
              <div className="pipeline-label">Interviews / Screens</div>
            </div>
            <div className="pipeline-arrow">➜</div>
            <div className="pipeline-step">
              <div className="pipeline-number">{offers}</div>
              <div className="pipeline-label">Offers</div>
            </div>
          </div>
          <div className="analysis-subtext">
            Target ratio: 30–50 apps → 1 offer. You’re tracking this in real
            time.
          </div>
        </div>
        <div className="analysis-card">
        <h3 className="section-title">Next Interview Prediction</h3>
        {!prediction ? (
          <div>Not enough history yet.</div>
        ) : (
          <div className="prediction-box">
            <p>
              Interview rate so far:{" "}
              <strong>{Math.round(prediction.interviewRate * 100)}%</strong>{" "}
              per application.
            </p>
            <p>
              Applications per day:{" "}
              <strong>{prediction.appsPerDay.toFixed(1)}</strong>
            </p>
            <p>
              Expected applications until next interview:{" "}
              <strong>
                {Math.round(prediction.expectedAppsForInterview)}
              </strong>
            </p>
            <p>
              Estimated date of next interview:{" "}
              <strong>{prediction.expectedDateStr}</strong>
            </p>
          </div>
        )}
        </div>

        <div className="analysis-card">
          <div className="analysis-title">Skills You Keep Matching</div>
          {topSkills.length === 0 ? (
            <div className="analysis-empty">No skills extracted yet.</div>
          ) : (
            <ul className="bar-list">
              {topSkills.map(([skill, count], idx) => (
                <li key={skill} className="bar-item">
                  <div className="bar-label">
                    {skill}
                    <span className="bar-count">×{count}</span>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill bar-fill-positive"
                      style={{ width: getBarWidth(idx, topSkills) }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
            <div className="analysis-section">
        <h3 className="section-title">Offer Probability by Job</h3>
        <p className="section-sub">
          Based on status, matchScore, and your overall conversion.
        </p>

        <div className="offer-table">
          <div className="offer-header">
            <span>Company / Job</span>
            <span>Status</span>
            <span>Match%</span>
            <span>Offer Chance</span>
          </div>
          {jobsWithProb.map((job) => (
            <div key={job.id} className="offer-row">
              <span className="offer-company">
                {job.company} — {job.job_title}
              </span>
              <span className="offer-status">{job.status}</span>
              <span className="offer-match">
                {job.matchScore != null ? `${job.matchScore}%` : "-"}
              </span>
              <span className="offer-score">
                {Math.round(job.offerProb * 100)}%
              </span>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default AnalysisDashboard;
